import React, { useState } from 'react';
import FormDatePicker from '../../components/common/form-date-picker';
import SearchableSelect from '../../components/common/searchable-select';
import BusinessUnitService from '../../services/businessUnitService';
import UserService from '../../services/userService';
import MessengerService from '../../services/messengerService';
import ReportsService from '../../services/reportsService';
import { showLoading, hideLoading } from '../../components/utils/swal-helpers';

// ── Constants ─────────────────────────────────────────────
const DATE_RANGE_OPTIONS = [
    { key: 'day',   label: '1 วัน'     },
    { key: 'week',  label: '1 สัปดาห์' },
    { key: 'month', label: '1 เดือน'   },
];

// ── Helpers ───────────────────────────────────────────────

/** แปลง Date เป็น string YYYY-MM-DD */
const toDateStr = (d) => {
    if (!d) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** คำนวณช่วงวันที่จาก range type */
const calcDateRange = (type) => {
    const today = new Date();
    if (type === 'day') return { start: today, end: today };
    if (type === 'week') {
        const start = new Date(today);
        start.setDate(today.getDate() - 6);
        return { start, end: today };
    }
    if (type === 'month') return {
        start: new Date(today.getFullYear(), today.getMonth(), 1),
        end: today,
    };
    return { start: null, end: null };
};

/** ดาวน์โหลดไฟล์จาก blob response — อ่านชื่อไฟล์จาก Content-Disposition header ถ้ามี */
const downloadBlob = (resp, fallbackName) => {
    const blob = new Blob([resp.data], { type: resp.headers['content-type'] });
    const disposition = resp.headers['content-disposition'];
    let filename = fallbackName;
    if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match?.[1]) filename = match[1];
    }
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

// ── Service Report Card ───────────────────────────────────
const ServiceReportCard = ({ businessUnits, departments }) => {
    const [startDate, setStartDate]       = useState(null);
    const [endDate, setEndDate]           = useState(null);
    const [selectedBu, setSelectedBu]     = useState('');
    const [selectedDept, setSelectedDept] = useState('');
    const [rangeType, setRangeType]       = useState('day');
    const [exporting, setExporting]       = useState(false);

    const handleRangeType = (type) => {
        const { start, end } = calcDateRange(type);
        setRangeType(type);
        setStartDate(start);
        setEndDate(end);
    };

    const handleExport = async () => {
        if (!startDate || !endDate) {
            alert('กรุณาเลือกวันที่เริ่มต้นและวันที่สิ้นสุด');
            return;
        }
        setExporting(true);
        showLoading();
        try {
            // หมายเหตุ: department ยังไม่ส่งไป เพราะ backend /reports/requests ยังไม่มี param นี้ (คุยกันทีหลัง)
            const resp = await ReportsService.ReportRequests({
                service_date_start: toDateStr(startDate),
                service_date_end: toDateStr(endDate),
                id_business_unit: selectedBu || undefined,
            });
            downloadBlob(resp, `service-report-${toDateStr(startDate)}_${toDateStr(endDate)}.xlsx`);
        } catch (err) {
            console.error('Export service report error:', err);
            alert('เกิดข้อผิดพลาดในการ Export');
        } finally {
            setExporting(false);
            hideLoading();
        }
    };

    return (
        <div className="rs-card">
            <div className="rs-card__header">
                <div className="rs-card__icon rs-card__icon--blue">
                    <i className="bx bxs-chart" />
                </div>
                <div>
                    <div className="rs-card__title">Service Report</div>
                    <div className="rs-card__sub">รายการปริมาณการใช้งานระบบ Mercury</div>
                </div>
            </div>

            {/* Row 1: วันที่ + เลือกแบบ */}
            <div className="rs-form-grid">
                <div className="rs-field">
                    <label className="rs-label">วันที่เริ่มต้น (Start Date)</label>
                    <FormDatePicker
                        value={startDate}
                        onChange={setStartDate}
                        placeholderText="DD/MM/YYYY"
                    />
                </div>
                <div className="rs-field">
                    <label className="rs-label">วันที่สิ้นสุด (End Date)</label>
                    <FormDatePicker
                        value={endDate}
                        onChange={setEndDate}
                        placeholderText="DD/MM/YYYY"
                    />
                </div>
                <div className="rs-field rs-field--range">
                    <label className="rs-label">เลือกแบบ</label>
                    <div className="rs-range-btns">
                        {DATE_RANGE_OPTIONS.map(r => (
                            <button
                                key={r.key}
                                className={`rs-range-btn ${rangeType === r.key ? 'rs-range-btn--active' : ''}`}
                                onClick={() => handleRangeType(r.key)}
                            >
                                {r.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Row 2: บริษัท + แผนก */}
            <div className="rs-form-grid">
                <div className="rs-field">
                    <label className="rs-label">บริษัท</label>
                    <SearchableSelect
                        id="service-report-bu"
                        name="business_unit"
                        value={selectedBu}
                        onChange={(e) => setSelectedBu(e.target.value)}
                        options={businessUnits.map(bu => ({ value: String(bu.id), label: bu.name_th }))}
                        placeholder="All"
                    />
                </div>
                <div className="rs-field">
                    <label className="rs-label">แผนก</label>
                    <SearchableSelect
                        id="service-report-dept"
                        name="department"
                        value={selectedDept}
                        onChange={(e) => setSelectedDept(e.target.value)}
                        options={departments.map(dept => ({ value: dept, label: dept }))}
                        placeholder="All"
                    />
                </div>
            </div>

            <div className="rs-card__footer">
                <button className="rs-export-btn" onClick={handleExport} disabled={exporting}>
                    <i className="mdi mdi-file-send-outline" /> {exporting ? 'กำลัง Export...' : 'Export'}
                </button>
            </div>
        </div>
    );
};

// ── Messenger Report Card ─────────────────────────────────
const MessengerReportCard = ({ messengers }) => {
    const [startDate, setStartDate]                 = useState(null);
    const [endDate, setEndDate]                     = useState(null);
    const [selectedMessenger, setSelectedMessenger] = useState('');
    const [exporting, setExporting]                 = useState(false);

    const handleExport = async () => {
        setExporting(true);
        showLoading();
        try {
            const resp = await ReportsService.ExportMessengerReport({
                service_date_start: startDate ? toDateStr(startDate) : undefined,
                service_date_end: endDate ? toDateStr(endDate) : undefined,
                id_messenger: selectedMessenger || undefined,
            });
            const rangeLabel = startDate ? `${toDateStr(startDate)}_${toDateStr(endDate)}` : 'current-month';
            downloadBlob(resp, `messenger-report-${rangeLabel}.xlsx`);
        } catch (err) {
            console.error('Export messenger report error:', err);
            alert('เกิดข้อผิดพลาดในการ Export');
        } finally {
            setExporting(false);
            hideLoading();
        }
    };

    return (
        <div className="rs-card">
            <div className="rs-card__header">
                <div className="rs-card__icon rs-card__icon--blue">
                    <i className="ri-donut-chart-fill" />
                </div>
                <div>
                    <div className="rs-card__title">Messenger Report</div>
                    <div className="rs-card__sub">รายการพนักงานรับส่งเอกสาร</div>
                </div>
            </div>

            {/* Row 1: วันที่ */}
            <div className="rs-form-grid">
                <div className="rs-field">
                    <label className="rs-label">วันที่เริ่มต้น (Start Date)</label>
                    <FormDatePicker
                        value={startDate}
                        onChange={setStartDate}
                        placeholderText="DD/MM/YYYY"
                    />
                </div>
                <div className="rs-field">
                    <label className="rs-label">วันที่สิ้นสุด (End Date)</label>
                    <FormDatePicker
                        value={endDate}
                        onChange={setEndDate}
                        placeholderText="DD/MM/YYYY"
                    />
                </div>
            </div>

            {/* Row 2: พนักงาน */}
            <div className="rs-form-grid">
                <div className="rs-field">
                    <label className="rs-label">พนักงาน</label>
                    <SearchableSelect
                        id="messenger-report-messenger"
                        name="messenger"
                        value={selectedMessenger}
                        onChange={(e) => setSelectedMessenger(e.target.value)}
                        options={messengers.map(m => ({ value: String(m.id), label: `${m.name} ${m.surname}` }))}
                        placeholder="All"
                    />
                </div>
            </div>

            <div className="rs-card__footer">
                <button className="rs-export-btn" onClick={handleExport} disabled={exporting}>
                    <i className="mdi mdi-file-send-outline" /> {exporting ? 'กำลัง Export...' : 'Export'}
                </button>
            </div>
        </div>
    );
};

// ── Main Component ────────────────────────────────────────
const AdminReportService = () => {
    const [businessUnits, setBusinessUnits] = useState([]);
    const [departments, setDepartments]     = useState([]);
    const [messengers, setMessengers]       = useState([]);

    React.useEffect(() => {
        loadBusinessUnits();
        loadDepartments();
        loadMessengers();
    }, []);

    const loadBusinessUnits = async () => {
        try {
            const resp = await BusinessUnitService.SearchBusinessUnit({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            setBusinessUnits(resp.data.result);
        } catch (err) { console.error(err); }
    };

    const loadDepartments = async () => {
        try {
            const resp = await UserService.SearchUser({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            const distinct = [...new Set(
                resp.data.result.map(u => u.department).filter(Boolean)
            )].sort();
            setDepartments(distinct);
        } catch (err) { console.error(err); }
    };

    const loadMessengers = async () => {
        try {
            const resp = await MessengerService.SearchMessenger({ status: 1 });
            if (!resp.data.is_success || !resp.data.result) return;
            setMessengers(resp.data.result);
        } catch (err) { console.error(err); }
    };

    return (
        <div className="mc-page">
            <div className="mc-header">
                <div className="mc-header__left">
                    <h2 className="mc-title">Report - Service Report</h2>
                    <p className="mc-subtitle">ภาพรวมสถิติและรายงานการใช้บริการ</p>
                </div>
            </div>

            <div className="rs-cards">
                <ServiceReportCard
                    businessUnits={businessUnits}
                    departments={departments}
                />
                <MessengerReportCard messengers={messengers} />
            </div>
        </div>
    );
};

export default AdminReportService;